/**
 * Owner: Masa Bayu
 * File: color-theme.ts
 * Date:Time: 2026-09-13 21:16
 * Description: 22C theme identity, mappings, and theme references.
 */
import { createQXThemeColorMapping, isQXColorRole } from './color-role';
const FORBIDDEN = [
    'css',
    'hex',
    'rgb',
    'hsl',
    'gradient',
    'opacity',
    'shadow',
    'className',
    'style',
    'dom',
    'react',
    'component',
];
function assertNoForbidden(record, label) {
    for (const key of FORBIDDEN) {
        if (key in record) {
            throw new Error(`${label} must not include render field "${key}"`);
        }
    }
}
function assertId(value, label) {
    if (typeof value !== 'string' || value.trim().length === 0 || /\s/.test(value)) {
        throw new Error(`${label} must be a non-empty id without whitespace`);
    }
}
export function createQXThemeReference(themeId) {
    assertId(themeId, 'Theme reference themeId');
    return Object.freeze({ themeId: themeId.trim() });
}
export function isQXThemeReference(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
    }
    return typeof value.themeId === 'string';
}
export function createQXTheme(options) {
    assertNoForbidden({ ...options }, 'Theme');
    assertId(options.id, 'Theme id');
    if (typeof options.name !== 'string' || options.name.trim().length === 0) {
        throw new Error('Theme name must be a non-empty string');
    }
    const list = [...options.mappings];
    const mappings = list.map((mapping) => {
        assertNoForbidden({ ...mapping }, 'Theme color mapping');
        return createQXThemeColorMapping(mapping.role, mapping.swatchId);
    });
    const seen = new Set();
    for (const mapping of mappings) {
        if (!isQXColorRole(mapping.role)) {
            throw new Error(`Invalid color role: ${String(mapping.role)}`);
        }
        if (seen.has(mapping.role)) {
            throw new Error(`Duplicate theme color role: ${mapping.role}`);
        }
        seen.add(mapping.role);
    }
    return Object.freeze({
        id: options.id.trim(),
        name: options.name.trim(),
        mappings: Object.freeze(mappings),
    });
}
export function isQXTheme(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
    }
    const record = value;
    return (typeof record.id === 'string' &&
        typeof record.name === 'string' &&
        Array.isArray(record.mappings));
}
//# sourceMappingURL=color-theme.js.map